import type { ReactNode } from 'react';
import { Skeleton, Stack, Typography } from '@mui/material';

import type { SelectFieldConfig } from '../../config';
import type { SectionKey } from '../../lease-details.types';
import { LeaseDetailsSelect } from '../../inputs/lease-details-select';
import { useResolvedOptions } from '../../hooks';
import { useFieldEventContextBuilder } from '../use-field-event-context';

export type SelectFieldProps = {
  path: string;
  field: SelectFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
  label: ReactNode;
  helperText: ReactNode;
};

export function SelectField({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
  label,
  helperText,
}: SelectFieldProps) {
  const { options, isLoading, isError } = useResolvedOptions(field.options);
  const buildContext = useFieldEventContextBuilder<string>(path, sectionKey, arrayIndex);

  if (isLoading) {
    return <Skeleton variant="rounded" height={48} width="100%" />;
  }

  if (isError) {
    return (
      <Stack direction="column" spacing={0.5}>
        {label ? <Typography variant="body2">{label}</Typography> : null}
        <Typography variant="body2" color="error">
          Failed to load options. Please try again.
        </Typography>
      </Stack>
    );
  }

  return (
    <LeaseDetailsSelect
      name={path}
      title={typeof label === 'string' ? label : ''}
      helperText={helperText}
      options={options}
      SelectItem={field.SelectItem}
      emptyOption={field.emptyOption}
      required={required}
      disabled={disabled}
      defaultValue={field.defaultValue}
      onSelect={(value) => field.events?.onChange?.(buildContext(value))}
    />
  );
}
